import React, { useState, useEffect } from 'react';
import './AccountMenu.css';
import { User, Trophy, LogOut, Check, X, Edit2, Shield, Zap, Star, Crown, Ghost, Mail, Key } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useAudio } from '../utils/AudioContext'; // Import Audio
import { 
    playUiHoverSound, 
    playUiClickSound, 
    playUiConfirmSound, 
    playUiOpenSound, 
    playUiCloseSound 
} from '../utils/sounds'; // Import Sounds
import { ref, get, update } from 'firebase/database';
import { db } from '../../firebase';

// --- Date Helpers ---
const getWeekId = () => {
    const d = new Date();
    d.setHours(0,0,0,0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(),0,1);
    const weekNo = Math.ceil(( ( (d - yearStart) / 86400000) + 1)/7);
    return `${d.getFullYear()}_${weekNo}`;
};

const getMonthId = () => {
    const d = new Date();
    return `${d.getFullYear()}_${d.getMonth() + 1}`;
};

const isSameWeek = (timestamp) => {
    if (!timestamp) return false;
    const d = new Date(timestamp);
    const now = new Date();
    const getW = (date) => {
        const d = new Date(date.valueOf());
        d.setHours(0,0,0,0);
        d.setDate(d.getDate() + 4 - (d.getDay() || 7));
        const ys = new Date(d.getFullYear(),0,1);
        return Math.ceil((((d - ys) / 86400000) + 1)/7);
    };
    return d.getFullYear() === now.getFullYear() && getW(d) === getW(now);
};

const isSameMonth = (timestamp) => {
    if (!timestamp) return false;
    const d = new Date(timestamp);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
};

// Google Icon SVG Component
const GoogleIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

export const AVATARS = [
    { id: 0, icon: <User />, name: "Default", color: "#ffffff" },
    { id: 1, icon: <Zap />, name: "Spark", color: "#facc15" },
    { id: 2, icon: <Shield />, name: "Guardian", color: "#60a5fa" },
    { id: 3, icon: <Star />, name: "Star", color: "#fbbf24" },
    { id: 4, icon: <Ghost />, name: "Spirit", color: "#e879f9" },
    { id: 5, icon: <Crown />, name: "Royal", color: "#f87171" },
];

function AccountMenu({ onClose }) {
    const { currentUser, userData, loginWithGoogle, loginWithEmail, registerWithEmail, logout, updateUserProfile } = useAuth();
    const { sfxMuted } = useAudio();
    
    // UI States
    const [isExiting, setIsExiting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [authMode, setAuthMode] = useState('login'); 
    const [signOutConfirm, setSignOutConfirm] = useState(false);
    const [hasNavigated, setHasNavigated] = useState(false); 
    
    // Data States
    const [rank, setRank] = useState('---');
    const [editUsername, setEditUsername] = useState('');
    const [editAvatar, setEditAvatar] = useState(0);
    const [formData, setFormData] = useState({ email: '', password: '', username: '' });
    const [error, setError] = useState('');

    // Play Open Sound on Mount
    useEffect(() => {
        playUiOpenSound(sfxMuted);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentUser && userData) {
            setEditUsername(userData.username || 'Player');
            setEditAvatar(userData.avatarId || 0);
            syncScores();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, userData]);

    const syncScores = async () => {
        if (!currentUser || !userData) return;
        
        try {
            const updates = {};
            const username = userData.username || 'Player';
            const avatarId = userData.avatarId || 0;

            const allTimeScore = userData.highscore?.alltime?.score || 0;
            if (allTimeScore > 0) {
                updates[`leaderboards/alltime/${currentUser.uid}`] = {
                    score: allTimeScore,
                    username,
                    avatarId
                };
            }

            const weeklyData = userData.highscore?.weekly;
            if (weeklyData && weeklyData.score > 0 && isSameWeek(weeklyData.timestamp)) {
                const weekId = getWeekId();
                updates[`leaderboards/weekly_${weekId}/${currentUser.uid}`] = {
                    score: weeklyData.score,
                    username,
                    avatarId
                };
            }

            const monthlyData = userData.highscore?.monthly;
            if (monthlyData && monthlyData.score > 0 && isSameMonth(monthlyData.timestamp)) {
                const monthId = getMonthId();
                updates[`leaderboards/monthly_${monthId}/${currentUser.uid}`] = {
                    score: monthlyData.score,
                    username,
                    avatarId
                };
            }

            if (Object.keys(updates).length > 0) {
                await update(ref(db), updates);
            }

            fetchRank(allTimeScore);

        } catch (err) {
            console.error("Error syncing scores:", err);
        }
    };

    const fetchRank = async (myScore) => {
        try {
            const lbRef = ref(db, 'leaderboards/alltime');
            const snapshot = await get(lbRef);
            if (snapshot.exists()) {
                const data = snapshot.val();
                const sorted = Object.keys(data)
                    .map(key => ({ uid: key, ...data[key] }))
                    .sort((a, b) => b.score - a.score);
                
                const myRank = sorted.findIndex(u => u.uid === currentUser.uid) + 1;
                setRank(myRank > 0 ? `#${myRank}` : 'N/A');
            } else {
                setRank(myScore > 0 ? '#1' : 'N/A');
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleClose = () => {
        playUiCloseSound(sfxMuted);
        setIsExiting(true);
    };

    const onAnimationEnd = (e) => { 
        if (e.target !== e.currentTarget) return; 
        if (isExiting && onClose) onClose(); 
    };

    const handleAuthSubmit = async (e) => {
        e.preventDefault();
        playUiConfirmSound(sfxMuted);
        setError('');
        try {
            if (authMode === 'login') {
                await loginWithEmail(formData.email, formData.password);
            } else {
                await registerWithEmail(formData.email, formData.password, formData.username);
            }
        } catch (err) {
            setError(err.message.replace('Firebase:', '').trim());
        }
    };

    const handleGoogleLogin = async () => {
        playUiConfirmSound(sfxMuted);
        try { await loginWithGoogle(); } catch (err) { setError("Failed to sign in with Google."); }
    };

    const switchToEdit = () => {
        playUiClickSound(sfxMuted);
        setHasNavigated(true);
        setIsEditing(true);
    };

    const switchToProfile = () => {
        playUiClickSound(sfxMuted);
        setIsEditing(false);
        setEditUsername(userData.username); 
        setEditAvatar(userData.avatarId);
    };

    const handleSaveProfile = async () => {
        playUiConfirmSound(sfxMuted);
        try {
            await updateUserProfile({ username: editUsername, avatarId: editAvatar });
            setIsEditing(false);
        } catch (err) { 
            console.error("Profile save error", err);
            setError("Failed to update profile."); 
        }
    };

    const handleSignOut = async () => {
        if (!signOutConfirm) { 
            playUiClickSound(sfxMuted);
            setSignOutConfirm(true); 
            return; 
        }
        playUiConfirmSound(sfxMuted);
        await logout();
        setSignOutConfirm(false);
    };

    const renderTitle = (text) => {
        return text.split('').map((char, index) => {
            const colors = ['c-green', 'c-blue', 'c-red', 'c-yellow'];
            const color = colors[index % colors.length];
            return <span key={index} className={color}>{char}</span>;
        });
    };

    const currentAvatarObj = AVATARS.find(a => a.id === (userData?.avatarId || 0)) || AVATARS[0];

    const getDisplayScore = () => {
        return userData?.highscore?.alltime?.score || 0;
    };

    return (
        <div className={`account-container ${isExiting ? 'fade-out' : 'fade-in'}`} onAnimationEnd={onAnimationEnd}>
            <div className={`account-modal ${isExiting ? 'pop-out' : ''}`}>
                
                <h1 className="modal-title">
                    {renderTitle(currentUser ? (isEditing ? 'EDITING' : 'PROFILE') : 'ACCOUNT')}
                </h1>

                {!currentUser ? (
                    <div className="auth-view">
                        <div className="auth-tabs">
                            <button 
                                className={`tab-btn ${authMode === 'login' ? 'active' : ''}`}
                                onClick={() => { playUiClickSound(sfxMuted); setAuthMode('login'); }}
                                onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            >Login</button>
                            <button 
                                className={`tab-btn ${authMode === 'register' ? 'active' : ''}`}
                                onClick={() => { playUiClickSound(sfxMuted); setAuthMode('register'); }}
                                onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            >Register</button>
                        </div>

                        <form onSubmit={handleAuthSubmit} className="auth-form">
                            <div className={`username-wrapper ${authMode === 'register' ? 'open' : ''}`}>
                                <div className="username-inner">
                                    <div className="input-group">
                                        <User size={18} />
                                        <input 
                                            type="text" placeholder="Username"
                                            value={formData.username}
                                            onChange={e => setFormData({...formData, username: e.target.value})}
                                            required={authMode === 'register'}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="input-group">
                                <Mail size={18} />
                                <input 
                                    type="email" placeholder="Email"
                                    value={formData.email}
                                    onChange={e => setFormData({...formData, email: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="input-group">
                                <Key size={18} />
                                <input 
                                    type="password" placeholder="Password"
                                    value={formData.password}
                                    onChange={e => setFormData({...formData, password: e.target.value})}
                                    required
                                />
                            </div>
                            
                            {error && <div className="error-msg">{error}</div>}

                            <button 
                                type="submit" 
                                className="btn btn-green"
                                onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            >
                                {authMode === 'login' ? 'Enter' : 'Join'}
                            </button>
                        </form>

                        <div className="divider"><span>OR</span></div>

                        <button 
                            type="button" 
                            className="btn btn-google" 
                            onClick={handleGoogleLogin}
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                        >
                            <GoogleIcon />
                            <span>Sign in with Google</span>
                        </button>
                    </div>
                ) : (
                    <div className="profile-view-container">
                        {isEditing ? (
                            <div className="view-slide slide-in-right">
                                <div className="edit-dashboard">
                                    <div className="edit-section">
                                        <span className="edit-label">Choose Avatar</span>
                                        <div className="avatar-selection-grid">
                                            {AVATARS.map(av => (
                                                <div 
                                                    key={av.id} 
                                                    className={`avatar-choice ${editAvatar === av.id ? 'selected' : ''}`}
                                                    onClick={() => { playUiClickSound(sfxMuted); setEditAvatar(av.id); }}
                                                    onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                                    style={editAvatar === av.id ? {
                                                        borderColor: av.color,
                                                        backgroundColor: `${av.color}15`,
                                                        boxShadow: `0 5px 15px ${av.color}20`
                                                    } : {}}
                                                >
                                                    <div style={{ color: av.color }}>{av.icon}</div>
                                                    <span 
                                                        className="avatar-name"
                                                        style={editAvatar === av.id ? { color: av.color } : {}}
                                                    >
                                                        {av.name}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="edit-section">
                                        <span className="edit-label">Username</span>
                                        <div className="input-group edit-input-group">
                                            <Edit2 size={18} />
                                            <input 
                                                type="text" 
                                                className="username-edit-input"
                                                value={editUsername}
                                                onChange={(e) => setEditUsername(e.target.value)}
                                                maxLength={15}
                                                placeholder="Enter Username"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="actions-stack">
                                    <button 
                                        className="btn btn-green" 
                                        onClick={handleSaveProfile}
                                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                    >
                                        <Check /> Save Changes
                                    </button>
                                    <button 
                                        className="btn btn-grey" 
                                        onClick={switchToProfile}
                                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                    >
                                        <X /> Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className={`view-slide ${hasNavigated ? 'slide-in-left' : ''}`}>
                                <div className="profile-card-layout">
                                    <div 
                                        className="avatar-large"
                                        style={{ 
                                            borderColor: currentAvatarObj.color,
                                            boxShadow: `0 0 25px ${currentAvatarObj.color}40`,
                                            color: currentAvatarObj.color
                                        }}
                                    >
                                        {currentAvatarObj.icon}
                                    </div>

                                    <div className="header-text-group">
                                        <h2 className="username-display">{userData?.username || 'Loading...'}</h2>
                                        <span className="email-display">{currentUser.email}</span>
                                    </div>
                                </div>

                                <div className="stats-grid">
                                    <div className="stat-card rank-card">
                                        <div className="stat-icon-bg"><Trophy /></div>
                                        <div className="stat-content">
                                            <span className="stat-label">Rank</span>
                                            <span className="stat-value">{rank}</span>
                                        </div>
                                    </div>
                                    <div className="stat-card score-card">
                                        <div className="stat-icon-bg"><Star /></div>
                                        <div className="stat-content">
                                            <span className="stat-label">Best Score</span>
                                            <span className="stat-value">{getDisplayScore()}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="actions-stack">
                                    <button 
                                        className="btn btn-blue" 
                                        onClick={switchToEdit}
                                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                    >
                                        <Edit2 size={18} /> Edit Profile
                                    </button>
                                    <button 
                                        className={`btn btn-red ${signOutConfirm ? 'shake' : ''}`} 
                                        onClick={handleSignOut}
                                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                                    >
                                        <LogOut size={18} /> {signOutConfirm ? 'Are you sure?' : 'Sign Out'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {!isEditing && (
                    <button 
                        className="btn btn-done" 
                        onClick={handleClose}
                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                    >
                        <Check /> Done
                    </button>
                )}
            </div>
        </div>
    );
}

export default AccountMenu;